import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { always, head } from 'ramda';
import {
  LOAD_PAYER,
  LOAD_PAYER_USER,
  cancelPalViewAsRefresh,
  isPalViewAsRefreshFallback,
  loadPayer,
  loadPayerUser,
  startPalViewAsRefresh,
} from 'model/viewAs';
import { NEW_TAB } from 'consts';
import { selector } from 'selectors/viewAs';
import { selector as fspConfigPayALoanSelector } from 'selectors/fspConfigPayALoan';
import { profileSelector } from 'selectors/payer';
import { selectorPayerId } from 'selectors/payerUser';
import { useDispatch, useSelector } from 'react-redux';
import { useFSPId } from 'hooks';
import { isEnrolledPalRoute } from 'util/payerOwnerType';
import { Paper, Popper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { withViewAsPayer } from 'components/common/permissions';
import useFlags from 'components/modules/implementation/Fsp/Detail/common/hooks/useFeatureFlags';
import PaymentIframe from 'components/modules/service/PalGuestPayment/PaymentIframe';
import PayerOrPayerUserButton, {
  usePayerUserButton,
} from '../../PayerOrPayerUserButton';
import EnrolledPalViewAsSession from './EnrolledPalViewAsSession';
import PalRefreshPanel from './PalRefreshPanel';
import Products from './Products';
import resolveEnrolledViewAsIdentity from './enrolledViewAsIdentity';

const PAL_VIEW_AS_REFRESH_FLAG = 'PZ.OntracV1.ViewAs.PAL';
// Stable reference: MUI v4 Popper destroys and recreates popper.js whenever `modifiers` changes.
const REFRESH_POPPER_MODIFIERS = {
  preventOverflow: { boundariesElement: 'viewport' },
};

const useStyles = makeStyles((theme) => ({
  refreshPopper: {
    zIndex: theme.zIndex.modal,
  },
  refreshPaper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: `calc(100vh - ${theme.spacing(8)}px)`,
    maxWidth: `calc(100vw - ${theme.spacing(4)}px)`,
    overflowY: 'auto',
    width: 444,
  },
}));

let requestSequence = 0;
const nextRequestId = () => {
  requestSequence += 1;
  return `${Date.now()}-${requestSequence}`;
};

const openEnrolledTab = () =>
  window.open(`${window.location.origin}/ifpPalPaymentLoading.html`, NEW_TAB);

const ViewAs = ({ fspId: fspIdProp, payerId, payerType, payerUser }) => {
  const classes = useStyles();
  const [anchorElLocal, setAnchorElLocal] = useState(null);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [submenu, setSubmenu] = useState(false);
  const [tabOpen, setTabOpen] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pendingLoad, setPendingLoad] = useState(null);
  const [enrolledSession, setEnrolledSession] = useState(null);
  const [backgroundPalDeliveries, setBackgroundPalDeliveries] = useState([]);
  const awaitingPanelRef = useRef(false);
  const enrolledSessionRef = useRef(null);
  const openedRequestIdRef = useRef(null);
  const pendingTabRef = useRef(null);
  const currentTargetRef = useRef(null);
  const backgroundPopupClosedCallbacksRef = useRef({});
  const { setAnchorEl = always, setDisabled = always } = usePayerUserButton();
  const dispatch = useDispatch();
  const fspId = useFSPId(fspIdProp);
  const viewAs = useSelector((_) => selector(_, payerId)) ?? {};
  const { registration, refresh, urls = [] } = viewAs;
  const payer = useSelector((state) => profileSelector(state, { id: payerId }));
  const fspConfigPayALoan = useSelector((state) =>
    fspConfigPayALoanSelector(state, fspId)
  );
  const payerUsers = useSelector((state) => selectorPayerId(state, payerId));
  const enrolledPalRoute = isEnrolledPalRoute(payer, fspConfigPayALoan);
  const {
    data: flagData,
    error: flagError,
    loading: flagLoading,
  } = useFlags(PAL_VIEW_AS_REFRESH_FLAG);
  const palRefreshEnabled =
    !flagLoading && !flagError && flagData?.FlagValue === true;
  const activeRequestIdRef = useRef(null);
  const palRefreshContextMatches =
    registration?.status === 'success' &&
    registration?.palRefreshEnabled === true &&
    Boolean(registration?.userId) &&
    Boolean(registration?.requestId) &&
    refresh?.requestId === registration.requestId;
  const palRefreshFallback =
    palRefreshContextMatches &&
    isPalViewAsRefreshFallback(refresh?.response?.status);
  const refreshRequestId = refresh?.requestId;
  const refreshSettled =
    palRefreshContextMatches &&
    !palRefreshFallback &&
    refresh?.pollingComplete === true;
  const enrolledTab =
    enrolledSession?.tab &&
    (enrolledSession.failed || !enrolledSession.tab.closed)
      ? enrolledSession.tab
      : null;
  const enrolledRefreshRequested = Boolean(enrolledSession?.refreshRequestId);
  const enrolledLaunchReady =
    Boolean(enrolledTab) &&
    (enrolledSession.refreshRequestId === null ||
      (enrolledRefreshRequested &&
        refresh?.requestId === registration?.requestId &&
        (refresh?.pollingComplete === true ||
          isPalViewAsRefreshFallback(refresh?.response?.status))));

  const cancelRefresh = useCallback(() => {
    if (activeRequestIdRef.current) {
      dispatch(
        cancelPalViewAsRefresh({ requestId: activeRequestIdRef.current })
      );
    }
  }, [dispatch]);

  const handleClose = useCallback(() => {
    const trigger = currentTarget;
    cancelRefresh();
    awaitingPanelRef.current = false;
    setAnchorEl(null);
    setAnchorElLocal(null);
    setCurrentTarget(null);
    setDisabled(false);
    setTabOpen(true);
    setPanelOpen(false);
    setBlocked(false);
    setSelectedProduct(null);
    setPendingLoad(null);
    if (trigger && typeof trigger.focus === 'function') {
      window.requestAnimationFrame(() => trigger.focus());
    }
  }, [cancelRefresh, currentTarget, setAnchorEl, setDisabled]);

  const openTab = useCallback(
    (url) => {
      const tab = window.open(url, NEW_TAB);
      if (tab && !tab.closed) {
        handleClose();
        return;
      }
      setBlocked(true);
      if (!awaitingPanelRef.current) {
        setPanelOpen(true);
      }
    },
    [handleClose]
  );

  const closePendingTab = useCallback(() => {
    const tab = pendingTabRef.current;
    pendingTabRef.current = null;
    if (tab && !tab.closed) {
      tab.close();
    }
  }, []);

  const sharedClickHandler = (event) => {
    if (typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    cancelRefresh();
    closePendingTab();
    setEnrolledSession(null);
    awaitingPanelRef.current = false;
    setCurrentTarget(event.currentTarget);
    setSubmenu(false);
    setTabOpen(false);
    setPanelOpen(false);
    setBlocked(false);
    setSelectedProduct(null);
  };

  const dispatchLoad = useCallback(
    ({ data, load }) => {
      dispatch(
        load(
          palRefreshEnabled
            ? { ...data, palRefreshEnabled: true, requestId: nextRequestId() }
            : data
        )
      );
    },
    [dispatch, palRefreshEnabled]
  );

  const requestLoad = (request) => {
    if (flagLoading) {
      setPendingLoad(request);
      return;
    }
    dispatchLoad(request);
  };

  useEffect(() => {
    if (pendingLoad && !flagLoading) {
      setPendingLoad(null);
      dispatchLoad(pendingLoad);
    }
  }, [dispatchLoad, flagLoading, pendingLoad]);

  useEffect(() => {
    enrolledSessionRef.current = enrolledSession;
  }, [enrolledSession]);

  useEffect(() => {
    currentTargetRef.current = currentTarget;
  }, [currentTarget]);

  const reserveEnrolledTab = useCallback(() => {
    if (!enrolledPalRoute) {
      return;
    }
    const pending = pendingTabRef.current;
    if (pending && !pending.closed) {
      return;
    }
    const opened = openEnrolledTab();
    pendingTabRef.current = opened && !opened.closed ? opened : null;
  }, [enrolledPalRoute]);

  const startEnrolledViewAs = (event, userId) => {
    const pending = pendingTabRef.current;
    const opened = pending && !pending.closed ? pending : openEnrolledTab();
    const tab = opened && !opened.closed ? opened : null;
    if (pendingTabRef.current !== tab) {
      closePendingTab();
    }
    pendingTabRef.current = tab;
    if (typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    cancelRefresh();
    setPendingLoad(null);
    setAnchorEl(null);
    setAnchorElLocal(null);
    setSubmenu(false);
    setTabOpen(true);
    setBlocked(false);
    setSelectedProduct(null);
    openedRequestIdRef.current = null;
    setCurrentTarget(event.currentTarget);
    setEnrolledSession({
      key: nextRequestId(),
      refreshRequestId: undefined,
      tab,
      userId,
    });
    setPanelOpen(true);
  };

  useEffect(() => () => closePendingTab(), [closePendingTab]);

  useEffect(() => {
    if (
      !enrolledSession ||
      enrolledSession.refreshRequestId !== undefined ||
      flagLoading
    ) {
      return;
    }
    const requestId = palRefreshEnabled ? nextRequestId() : null;
    if (requestId) {
      dispatch(
        startPalViewAsRefresh({
          enabled: true,
          fspId,
          payerId,
          requestId,
          userId: enrolledSession.userId,
        })
      );
    }
    setEnrolledSession(
      (current) => current && { ...current, refreshRequestId: requestId }
    );
  }, [
    dispatch,
    enrolledSession,
    flagLoading,
    fspId,
    palRefreshEnabled,
    payerId,
  ]);

  const handleEnrolledClose = useCallback(() => {
    closePendingTab();
    setEnrolledSession(null);
    handleClose();
  }, [closePendingTab, handleClose]);

  const handleEnrolledOpen = useCallback(() => {
    const tab = openEnrolledTab();
    if (tab && !tab.closed) {
      pendingTabRef.current = tab;
      setEnrolledSession((current) => current && { ...current, tab });
    }
  }, []);

  const removeBackgroundDelivery = useCallback((key) => {
    setBackgroundPalDeliveries((deliveries) =>
      deliveries.filter((delivery) => delivery.key !== key)
    );
    delete backgroundPopupClosedCallbacksRef.current[key];
    const trigger = currentTargetRef.current;
    if (trigger && typeof trigger.focus === 'function') {
      window.requestAnimationFrame(() => trigger.focus());
    }
  }, []);

  const getBackgroundPopupClosedCallback = useCallback(
    (key) => {
      const cache = backgroundPopupClosedCallbacksRef.current;
      if (!cache[key]) {
        cache[key] = () => removeBackgroundDelivery(key);
      }
      return cache[key];
    },
    [removeBackgroundDelivery]
  );

  const handleEnrolledJwtDelivered = useCallback(
    ({ jwt, assertionBaseUrl }) => {
      const current = enrolledSessionRef.current;
      pendingTabRef.current = null;
      if (current) {
        setBackgroundPalDeliveries((deliveries) => [
          ...deliveries,
          {
            assertionBaseUrl,
            fspId,
            jwt,
            key: current.key,
            payerId,
            tab: current.tab,
            userId: current.userId,
          },
        ]);
      }
      setEnrolledSession(null);
      setPanelOpen(false);
      setAnchorEl(null);
      setDisabled(false);
    },
    [fspId, payerId, setAnchorEl, setDisabled]
  );

  const handleEnrolledError = useCallback(() => {
    setEnrolledSession((current) => current && { ...current, failed: true });
  }, []);

  const handlePayerClick = (event) => {
    const identity =
      enrolledPalRoute &&
      resolveEnrolledViewAsIdentity({ payerId, payerUsers });
    if (identity) {
      startEnrolledViewAs(event, identity.userId);
      return;
    }
    requestLoad({ data: { fspId, payerId }, load: loadPayer });
    sharedClickHandler(event);
  };

  const handlePayerUserClick = (event, payerUser) => {
    const identity =
      enrolledPalRoute && resolveEnrolledViewAsIdentity({ payerId, payerUser });
    if (identity) {
      startEnrolledViewAs(event, identity.userId);
      return;
    }
    requestLoad({
      data: { fspId, payerId, userId: payerUser.userId, payerUser },
      load: loadPayerUser,
    });
    sharedClickHandler(event);
  };

  useEffect(() => {
    activeRequestIdRef.current = registration?.requestId ?? null;
  }, [registration?.requestId]);

  useEffect(
    () => () => {
      cancelRefresh();
    },
    [cancelRefresh]
  );

  useEffect(() => {
    if (
      enrolledSession ||
      registration?.status !== 'success' ||
      registration?.palRefreshEnabled !== true ||
      !registration.requestId ||
      !registration.userId ||
      refresh?.requestId === registration.requestId
    ) {
      return;
    }

    dispatch(
      startPalViewAsRefresh({
        enabled: true,
        fspId: registration.fspId ?? fspId,
        payerId,
        requestId: registration.requestId,
        userId: registration.userId,
      })
    );
  }, [
    dispatch,
    enrolledSession,
    fspId,
    payerId,
    refresh?.requestId,
    registration?.fspId,
    registration?.palRefreshEnabled,
    registration?.requestId,
    registration?.status,
    registration?.userId,
    refresh,
  ]);

  useEffect(() => {
    if (enrolledSession || tabOpen || pendingLoad) {
      return;
    }

    const legacyFallback =
      registration?.palRefreshEnabled !== true || !registration?.userId;

    if (legacyFallback && urls.length === 1) {
      window.open(head(urls).url, NEW_TAB);
      handleClose();
      return;
    }

    const settledProduct = urls.length === 1 ? head(urls) : selectedProduct;
    if (
      (refreshSettled || palRefreshFallback) &&
      settledProduct?.url &&
      openedRequestIdRef.current !== refreshRequestId
    ) {
      openedRequestIdRef.current = refreshRequestId;
      openTab(settledProduct.url);
      return;
    }

    if (urls.length > 1) {
      if (legacyFallback && selectedProduct?.url) {
        window.open(selectedProduct.url, NEW_TAB);
        handleClose();
        return;
      }
      if (legacyFallback || (palRefreshFallback && !selectedProduct)) {
        setPanelOpen(false);
        setAnchorElLocal(currentTarget);
        return;
      }
      if (!panelOpen && !selectedProduct) {
        setAnchorElLocal(currentTarget);
      }
      return;
    }

    if (palRefreshContextMatches && urls.length === 1) {
      setSelectedProduct((current) => current ?? head(urls));
      setPanelOpen(true);
    }
  }, [
    currentTarget,
    enrolledSession,
    handleClose,
    openTab,
    panelOpen,
    palRefreshContextMatches,
    palRefreshFallback,
    pendingLoad,
    refreshRequestId,
    refreshSettled,
    registration?.palRefreshEnabled,
    registration?.userId,
    selectedProduct,
    tabOpen,
    urls,
  ]);

  const handleProductSelect = useCallback(
    (product) => {
      setSelectedProduct(product);
      awaitingPanelRef.current = true;
      setAnchorElLocal(null);
      if (refreshSettled || palRefreshFallback) {
        openedRequestIdRef.current = refreshRequestId;
        openTab(product.url);
      }
    },
    [openTab, palRefreshFallback, refreshRequestId, refreshSettled]
  );

  const handleProductsExited = useCallback(() => {
    if (awaitingPanelRef.current) {
      awaitingPanelRef.current = false;
      setPanelOpen(true);
    }
  }, []);

  const productUrl = (selectedProduct ?? head(urls))?.url;
  const handleOpen = useCallback(() => {
    window.open(productUrl, NEW_TAB);
    handleClose();
  }, [handleClose, productUrl]);

  const handleChangeProduct = useCallback(() => {
    awaitingPanelRef.current = false;
    setPanelOpen(false);
    setBlocked(false);
    setSelectedProduct(null);
    setAnchorElLocal(currentTarget);
  }, [currentTarget]);

  const handleBack = useCallback(() => {
    if (urls.length > 1) {
      handleChangeProduct();
      return;
    }
    handleClose();
  }, [handleChangeProduct, handleClose, urls.length]);

  const refreshPanelProps = enrolledSession
    ? {
        blocked: !enrolledTab,
        canChangeProduct: false,
        onBack: handleEnrolledClose,
        onCancel: handleEnrolledClose,
        onOpen: handleEnrolledOpen,
        refresh: enrolledRefreshRequested ? refresh : null,
      }
    : {
        blocked,
        canChangeProduct: urls.length > 1,
        onBack: handleBack,
        onCancel: handleClose,
        onOpen: handleOpen,
        refresh,
      };

  return (
    <>
      <PayerOrPayerUserButton
        fspId={fspId}
        onPersonUsersPrefetch={reserveEnrolledTab}
        onPersonUsersPrefetchRelease={closePendingTab}
        onPayerClick={handlePayerClick}
        onPayerUserClick={handlePayerUserClick}
        payerAction={LOAD_PAYER}
        payerButtonText="View As Payer"
        payerId={payerId}
        payerType={payerType}
        payerUser={payerUser}
        payerUserAction={LOAD_PAYER_USER}
        payerUserButtonText="View As User"
        selector={selector}
        submenu={urls.length > 1}
      />
      <Products
        anchorEl={anchorElLocal}
        onClose={handleClose}
        onExited={handleProductsExited}
        onProductSelect={handleProductSelect}
        payerId={payerId}
        palRefreshEnabled={palRefreshContextMatches}
        submenu={submenu}
      />
      {(panelOpen || enrolledSession) && (
        <Popper
          anchorEl={currentTarget}
          className={classes.refreshPopper}
          keepMounted
          modifiers={REFRESH_POPPER_MODIFIERS}
          open={panelOpen}
          placement="bottom-end"
        >
          <Paper
            className={classes.refreshPaper}
            elevation={8}
            onClick={(event) => event.stopPropagation()}
          >
            {enrolledLaunchReady ? (
              <EnrolledPalViewAsSession
                key={enrolledSession.key}
                fspId={fspId}
                onClose={handleEnrolledClose}
                onError={handleEnrolledError}
                onSuccess={handleEnrolledJwtDelivered}
                payerId={payerId}
                tab={enrolledSession.tab}
                userId={enrolledSession.userId}
              />
            ) : (
              <PalRefreshPanel {...refreshPanelProps} />
            )}
          </Paper>
        </Popper>
      )}
      {backgroundPalDeliveries.map(({ assertionBaseUrl, jwt, key, tab }) => (
        <PaymentIframe
          key={key}
          assertionBaseUrl={assertionBaseUrl}
          fspId={fspId}
          ifpAssertionVariant="enrolled"
          jwt={jwt}
          keepWindowFocused={false}
          onPopupClosed={getBackgroundPopupClosedCallback(key)}
          preOpenedPopup={tab}
        />
      ))}
    </>
  );
};

ViewAs.propTypes = {
  fspId: PropTypes.number,
  payerId: PropTypes.number.isRequired,
  payerType: PropTypes.string,
  payerUser: PropTypes.object,
};

ViewAs.defaultProps = {
  fspId: undefined,
  payerType: null,
  payerUser: null,
};

export default withViewAsPayer(ViewAs);
